import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { CalendarIcon, ChartBarIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import { toastError } from "../utils/toast";
import {
  aggregateServiceReports,
  filterReportsByRange,
  formatServiceDuration,
  getMonthKeysInRange,
  getPeriodRange,
  isValidDateRange,
  toDateOnly,
} from "../utils/serviceReportTotals";

const PERIODS = [
  { id: "weekly", label: "Settimanale" },
  { id: "monthly", label: "Mensile" },
  { id: "custom", label: "Personalizzato" },
];

const formatRange = (range) => {
  if (!isValidDateRange(range)) return "Intervallo non valido";
  const format = (value) =>
    new Date(`${value}T00:00:00`).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  return `${format(range.start)} – ${format(range.end)}`;
};

const TotalsCard = ({ label, value, detail }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
      {label}
    </p>
    <p className="mt-2 text-2xl font-semibold text-gray-900">{value}</p>
    {detail ? <p className="mt-1 text-xs text-gray-500">{detail}</p> : null}
  </div>
);

const RapportiServizioTotaliPreview = ({ embedded = false }) => {
  const { user, activeCongregazione } = useAuth();
  const isAdmin = user?.ruolo === "admin" || user?.ruolo === "super_admin";
  const today = useMemo(() => toDateOnly(new Date()), []);
  const [period, setPeriod] = useState("monthly");
  const [customRange, setCustomRange] = useState(() => ({
    start: toDateOnly(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
    end: toDateOnly(new Date()),
  }));
  const [state, setState] = useState({ loading: true, error: null, totals: null });
  const requestIdRef = useRef(0);

  const range = useMemo(
    () => getPeriodRange(period, customRange),
    [period, customRange]
  );

  const loadTotals = useCallback(async () => {
    if (!activeCongregazione?.id || !isValidDateRange(range)) {
      return;
    }

    const requestId = ++requestIdRef.current;
    setState({ loading: true, error: null, totals: null });
    try {
      const monthKeys = getMonthKeysInRange(range.start, range.end);
      const responses = await Promise.all(
        monthKeys.map((mese) => api.get(`/rapporti-servizio/admin?mese=${mese}`))
      );
      if (requestId !== requestIdRef.current) return;

      const reports = responses.flatMap((response) =>
        Array.isArray(response.data?.rapporti) ? response.data.rapporti : []
      );
      setState({
        loading: false,
        error: null,
        totals: aggregateServiceReports(filterReportsByRange(reports, range)),
      });
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      const message =
        error.response?.data?.message || "Impossibile caricare i totali dei rapporti";
      console.error("Errore caricamento totali rapporti:", error);
      toastError(message);
      setState({ loading: false, error: message, totals: null });
    }
  }, [activeCongregazione?.id, range]);

  useEffect(() => {
    if (!isValidDateRange(range)) {
      setState({ loading: false, error: "Seleziona un intervallo di date valido.", totals: null });
      return;
    }
    loadTotals();
  }, [loadTotals, range]);

  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className={embedded ? "w-full" : "px-4 py-6 sm:px-6 lg:px-8 space-y-6"}>
      {!embedded && <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <ChartBarIcon className="h-7 w-7 text-primary-600" />
              <h1 className="text-2xl font-semibold text-gray-900">
                Totali rapporti di servizio
              </h1>
            </div>
            <p className="mt-2 text-sm text-gray-600">
              {embedded
                ? "Somma solo i rapporti della congregazione o del servizio speciale attivo."
                : "Anteprima riservata agli amministratori: somma solo i rapporti della congregazione o del servizio speciale attivo."}
            </p>
          </div>
          <Link
            to="/volontari/rapporti-servizio"
            className="inline-flex items-center justify-center rounded-md border border-primary-600 px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50"
          >
            Torna ai rapporti
          </Link>
        </div>
        <div className="accent-line mt-4" />
      </div>}

      <section className="rounded-lg bg-white p-6 shadow-lg">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">Periodo</p>
            <div className="mt-2 flex flex-wrap gap-2" role="tablist" aria-label="Filtro periodo">
              {PERIODS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={period === item.id}
                  onClick={() => setPeriod(item.id)}
                  className={`rounded-md px-4 py-2 text-sm font-medium transition ${
                    period === item.id
                      ? "bg-primary-600 text-white"
                      : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          {period === "custom" ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="text-xs font-semibold uppercase text-gray-600">
                Dal
                <input
                  type="date"
                  value={customRange.start}
                  max={today}
                  onChange={(event) => setCustomRange((current) => ({ ...current, start: event.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm normal-case"
                />
              </label>
              <label className="text-xs font-semibold uppercase text-gray-600">
                Al
                <input
                  type="date"
                  value={customRange.end}
                  max={today}
                  onChange={(event) => setCustomRange((current) => ({ ...current, end: event.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm normal-case"
                />
              </label>
            </div>
          ) : null}
        </div>

        <div className="mt-5 flex items-center gap-2 rounded-lg border border-primary-100 bg-primary-50 px-4 py-3 text-sm text-primary-900">
          <CalendarIcon className="h-5 w-5 shrink-0 text-primary-600" />
          <span>
            Intervallo considerato: <strong>{formatRange(range)}</strong>
          </span>
        </div>

        {state.loading ? (
          <p className="py-12 text-center text-sm text-gray-500">Calcolo dei totali in corso...</p>
        ) : state.error ? (
          <div className="py-10 text-center">
            <p className="text-sm text-red-600">{state.error}</p>
            <button type="button" onClick={loadTotals} className="mt-4 rounded-md border border-primary-600 px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50">
              Riprova
            </button>
          </div>
        ) : state.totals ? (
          <>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              <TotalsCard label="Rapporti inseriti" value={state.totals.reportCount} />
              <TotalsCard label="Proclamatori" value={state.totals.volunteerCount} detail="Con almeno un rapporto nel periodo" />
              <TotalsCard label="Ore di servizio" value={formatServiceDuration(state.totals.serviceMinutes)} />
              <TotalsCard label="Ore LDC" value={state.totals.oreLdc} />
              <TotalsCard label="Studi biblici" value={state.totals.studi} />
              <TotalsCard label="Pubblicazioni" value={state.totals.publications} detail="Copie complessive lasciate" />
            </div>

            <div className="mt-6 rounded-lg border border-gray-200">
              <div className="border-b bg-gray-50 px-4 py-3">
                <h2 className="text-sm font-semibold text-gray-900">Dettaglio pubblicazioni</h2>
              </div>
              {state.totals.publicationBreakdown.length ? (
                <ul className="divide-y divide-gray-100">
                  {state.totals.publicationBreakdown.map((publication) => (
                    <li key={publication.key} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                      <span className="text-gray-700">
                        {publication.title}
                        {publication.language ? ` (${publication.language})` : ""}
                      </span>
                      <span className="font-semibold text-gray-900">{publication.copies}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-4 py-6 text-sm text-gray-500">Nessuna pubblicazione registrata nel periodo.</p>
              )}
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
};

export default RapportiServizioTotaliPreview;
